import React from 'react';
import { observer } from 'mobx-react';
import { Card } from '@/elements/shared/Card/Card';
import { PRODUCT_IMAGES } from '../mockData';
import styles from './StyleGuideCards.module.css';

const IMG = PRODUCT_IMAGES.badgeScanner;

type ImageFit = 'contain' | 'cover';
const FIT_VARIANTS: ImageFit[] = ['contain', 'cover'];

/**
 * Card Grid tab — mirrors Figma "Card / Grid" (node-id: 3:3149).
 *
 * Shows how cards behave in flex layouts and at different container widths.
 * Each demo panel is split to show both imageFit variants (contain vs cover).
 */
export const StyleGuideCardGrid: React.FC = observer(function StyleGuideCardGrid() {
  return (
    <div className={styles.section}>
      {/* ================================================================== */}
      {/* FLEX SIZE — VERTICAL */}
      {/* ================================================================== */}
      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Flex size</h3>
        <div className={styles.demo}>
          <div className={styles.fitVariants}>
            {FIT_VARIANTS.map((fit) => (
              <div key={fit} className={styles.fitVariant}>
                <span className={styles.fitLabel}>{fit}</span>
                <div className={styles.layoutDemo}>
                  <div className={styles.layoutRow}>
                    <span className={styles.layoutLabel}>3 flex cards</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '370px' }}>
                      {[1, 2, 3].map((i) => (
                        <div key={i} style={{ flex: '1 0 96px' }}>
                          <Card
                            orientation="vertical"
                            label="Badge Scanner"
                            imageSrc={IMG}
                            imageFit={fit}
                            value={`${fit}-flex-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={styles.layoutRow}>
                    <span className={styles.layoutLabel}>4 flex cards (wraps at narrow widths)</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '430px' }}>
                      {['Badge Scanner', 'Registration Desk', 'Information Booth', 'Lost'].map((label, i) => (
                        <div key={i} style={{ flex: '1 0 96px' }}>
                          <Card
                            orientation="vertical"
                            label={label}
                            imageSrc={IMG}
                            imageFit={fit}
                            value={`${fit}-flex-w-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* HORIZONTAL */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Horizontal</h2>
      </div>

      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Flex size</h3>
        <div className={styles.demo}>
          <div className={styles.fitVariants}>
            {FIT_VARIANTS.map((fit) => (
              <div key={fit} className={styles.fitVariant}>
                <span className={styles.fitLabel}>{fit}</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: '430px' }}>
                  <Card
                    orientation="horizontal"
                    label="Badge Scanner"
                    imageSrc={IMG}
                    imageFit={fit}
                    value={`${fit}-hlist-1`}
                  />
                  <Card
                    orientation="horizontal"
                    label="Registration Desk"
                    description="Effortlessly load your printer tray with access tickets (Z-fold) of various sizes."
                    cost="145,00 $"
                    imageSrc={IMG}
                    imageFit={fit}
                    value={`${fit}-hlist-2`}
                  />
                  <Card
                    orientation="horizontal"
                    label="Event Schedule"
                    imageSrc={IMG}
                    imageFit={fit}
                    value={`${fit}-hlist-3`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* CONTAINER WIDTH DEMO */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Container Width Demo</h2>
      </div>

      {/* 4 in row — max 428px container */}
      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>4 in row — container: 428px</h3>
        <div className={styles.demo}>
          <div className={styles.fitVariants}>
            {FIT_VARIANTS.map((fit) => (
              <div key={fit} className={styles.fitVariant}>
                <span className={styles.fitLabel}>{fit}</span>
                <div className={styles.layoutRow}>
                  <span className={styles.layoutLabel}>card: (428 - 12) / 4 = 104px</span>
                  <div
                    className="cp-products--card"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                      gap: '4px',
                      width: '428px',
                    }}
                  >
                    {[1, 2, 3, 4].map((i) => (
                      <Card
                        key={i}
                        orientation="vertical"
                        label="Badge Scanner"
                        imageSrc={IMG}
                        imageFit={fit}
                        value={`${fit}-cw-4-${i}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 3 in row — 328px to 427px */}
      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>3 in row — container: 328px to 427px</h3>
        <div className={styles.demo}>
          <div className={styles.fitVariants}>
            {FIT_VARIANTS.map((fit) => (
              <div key={fit} className={styles.fitVariant}>
                <span className={styles.fitLabel}>{fit}</span>
                <div className={styles.layoutDemo}>
                  <div className={styles.layoutRow}>
                    <span className={styles.layoutLabel}>min 328px — card: (328 - 8) / 3 = 107px</span>
                    <div
                      className="cp-products--card"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: '4px',
                        width: '328px',
                      }}
                    >
                      {[1, 2, 3].map((i) => (
                        <Card
                          key={i}
                          orientation="vertical"
                          label="Badge Scanner"
                          imageSrc={IMG}
                          imageFit={fit}
                          value={`${fit}-cw-3min-${i}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className={styles.layoutRow}>
                    <span className={styles.layoutLabel}>max 427px — card: (427 - 8) / 3 = 140px</span>
                    <div
                      className="cp-products--card"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: '4px',
                        width: '427px',
                      }}
                    >
                      {[1, 2, 3].map((i) => (
                        <Card
                          key={i}
                          orientation="vertical"
                          label="Badge Scanner"
                          imageSrc={IMG}
                          imageFit={fit}
                          value={`${fit}-cw-3max-${i}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 2 in row — 244px to 327px */}
      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>2 in row — container: 244px to 327px</h3>
        <div className={styles.demo}>
          <div className={styles.fitVariants}>
            {FIT_VARIANTS.map((fit) => (
              <div key={fit} className={styles.fitVariant}>
                <span className={styles.fitLabel}>{fit}</span>
                <div className={styles.layoutDemo}>
                  <div className={styles.layoutRow}>
                    <span className={styles.layoutLabel}>min 244px — card: (244 - 4) / 2 = 120px</span>
                    <div
                      className="cp-products--card"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '4px',
                        width: '244px',
                      }}
                    >
                      {[1, 2].map((i) => (
                        <Card
                          key={i}
                          orientation="vertical"
                          label="Badge Scanner"
                          imageSrc={IMG}
                          imageFit={fit}
                          value={`${fit}-cw-2min-${i}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className={styles.layoutRow}>
                    <span className={styles.layoutLabel}>max 327px — card: (327 - 4) / 2 = 162px</span>
                    <div
                      className="cp-products--card"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '4px',
                        width: '327px',
                      }}
                    >
                      {[1, 2].map((i) => (
                        <Card
                          key={i}
                          orientation="vertical"
                          label="Badge Scanner"
                          imageSrc={IMG}
                          imageFit={fit}
                          value={`${fit}-cw-2max-${i}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});
